import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart3, ChevronLeft, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { api, formatError } from "@/lib/api";
import { fmtCHF, fmtQty } from "@/lib/format";
import { Card, Empty, PageHeader, Spinner } from "@/components/Field";
import { StatsRecordDialog } from "@/components/dialogs/StatsRecordDialog";

const Money = ({ v, testId }) => <span data-testid={testId} className={v < 0 ? "text-red-700" : ""}>{v < 0 ? `− ${fmtCHF(-v)}` : fmtCHF(v)}</span>;

const YearsTable = ({ years, canEdit, onEdit, onDelete }) => (
  <Card testId="stats-years" className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-left text-slate-600"><tr><th className="px-4 py-3">Anno</th><th className="px-4 py-3">Eventi</th><th className="px-4 py-3 text-right">Costi</th><th className="px-4 py-3 text-right">Contanti</th><th className="px-4 py-3 text-right">Carte</th><th className="px-4 py-3 text-right">TWINT</th><th className="px-4 py-3 text-right">Fatture</th><th className="px-4 py-3 text-right">Incassi</th><th className="px-4 py-3 text-right">Saldo</th>{canEdit && <th className="px-4 py-3" />}</tr></thead>
      <tbody className="divide-y">
        {years.map((y) => (
          <tr key={y.year} data-testid={`stats-year-${y.year}`}>
            <td className="px-4 py-3 font-semibold">{y.year}</td>
            <td className="px-4 py-3 text-slate-600">{y.events.map((e) => e.name).join(", ") || <span className="text-slate-400">solo consuntivo</span>}</td>
            <td className="px-4 py-3 text-right"><Money v={y.cost} /></td>
            <td className="px-4 py-3 text-right"><Money v={y.cash} /></td>
            <td className="px-4 py-3 text-right"><Money v={y.card} /></td>
            <td className="px-4 py-3 text-right"><Money v={y.twint} /></td>
            <td className="px-4 py-3 text-right"><Money v={y.invoices} /></td>
            <td className="px-4 py-3 text-right font-medium"><Money v={y.revenue} testId={`stats-revenue-${y.year}`} /></td>
            <td className="px-4 py-3 text-right font-semibold"><Money v={y.balance} testId={`stats-balance-${y.year}`} /></td>
            {canEdit && (
              <td className="px-2 py-2 whitespace-nowrap text-right">
                <Button data-testid={`stats-record-edit-${y.year}`} variant="ghost" size="sm" className="h-9" onClick={() => onEdit(y.year)}><Pencil className="h-4 w-4 mr-1" />{y.has_record ? "Consuntivo" : "Aggiungi"}</Button>
                {y.has_record && <Button data-testid={`stats-record-delete-${y.year}`} variant="ghost" size="icon" className="h-9 w-9 text-red-700" onClick={() => onDelete(y.year)} aria-label="Elimina consuntivo"><Trash2 className="h-4 w-4" /></Button>}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </Card>
);

const ProductsTable = ({ d }) => {
  const years = d.years.map((y) => String(y.year));
  if (!d.products.length) return <Empty text="Nessuna uscita di magazzino collegata a questi eventi." testId="stats-no-products" />;
  return (
    <Card testId="stats-products" className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-slate-600">
          <tr><th className="px-4 py-3">Prodotto</th>{years.map((y) => <th key={y} className="px-4 py-3 text-right">{y}</th>)}<th className="px-4 py-3 text-right">Media</th><th className="px-4 py-3 text-right">Previsto (+{d.margin}%)</th><th className="px-4 py-3 text-right">In magazzino</th><th className="px-4 py-3 text-right">Da ordinare</th></tr>
        </thead>
        <tbody className="divide-y">
          {d.products.map((p) => (
            <tr key={p.key} data-testid={`stats-product-${p.key}`}>
              <td className="px-4 py-3 font-medium">{p.name} <span className="text-slate-400">({p.unit})</span>{p.custom && <div className="text-xs text-slate-500">personalizzato</div>}</td>
              {years.map((y) => <td key={y} className="px-4 py-3 text-right">{fmtQty(p.per_year[y])}</td>)}
              <td className="px-4 py-3 text-right">{fmtQty(p.average)}</td>
              <td className="px-4 py-3 text-right">{fmtQty(p.suggested)}</td>
              <td className="px-4 py-3 text-right">{p.custom ? "—" : fmtQty(p.stock)}</td>
              <td className="px-4 py-3 text-right font-semibold text-primary" data-testid={`stats-order-${p.key}`}>{fmtQty(p.to_order)}{p.order_cost ? <div className="text-xs font-normal text-slate-500">{fmtCHF(p.order_cost)}</div> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {d.order_total > 0 && <div data-testid="stats-order-total" className="px-4 py-3 border-t text-right font-semibold">Costo stimato dell'ordine: {fmtCHF(d.order_total)}</div>}
    </Card>
  );
};

const Detail = ({ name, onBack }) => {
  const { can } = useAuth();
  const [margin, setMargin] = useState(10);
  const [rec, setRec] = useState(null);
  const { data: d, error, reload } = useFetch(`/stats/series/detail?name=${encodeURIComponent(name)}&margin=${Number(margin) || 0}`, [margin]);
  const canEdit = can("stats", "edit");
  useEffect(() => { if (error) onBack(); }, [error, onBack]);
  const del = async (year) => {
    if (!window.confirm(`Eliminare il consuntivo ${year}?`)) return;
    try { await api.delete("/stats/records", { params: { series: name, year } }); toast.success("Consuntivo eliminato"); reload(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-5" data-testid="stats-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button data-testid="stats-back" variant="ghost" className="h-11 -ml-3" onClick={onBack}><ChevronLeft className="h-4 w-4 mr-1" /> Tutte le serie</Button>
        <label className="flex items-center gap-2 text-sm">Scorta extra %<Input data-testid="stats-margin" type="number" min="0" max="200" value={margin} onChange={(e) => setMargin(e.target.value)} className="h-11 w-20" /></label>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-2xl font-bold text-slate-900">{name}</h2>
        {canEdit && <Button data-testid="stats-add-record" variant="outline" className="h-11" onClick={() => setRec({ year: null })}><Plus className="h-4 w-4 mr-1" /> Consuntivo di un altro anno</Button>}
      </div>
      {!d ? <Spinner /> : (
        <>
          <YearsTable years={d.years} canEdit={canEdit} onEdit={(year) => setRec({ year })} onDelete={del} />
          <h3 className="font-heading text-lg font-semibold">Prodotti usati e cosa ordinare per il prossimo anno</h3>
          <p className="text-sm text-slate-500">Uscite di magazzino (uscite + danneggiati − resi) più le righe dei consuntivi. Previsto = media degli anni + scorta extra; da ordinare = previsto − magazzino attuale (per i personalizzati: quanto preparare).</p>
          <ProductsTable d={d} />
        </>
      )}
      {rec && <StatsRecordDialog open onOpenChange={(o) => !o && setRec(null)} series={name} year={rec.year} onSaved={reload} />}
    </div>
  );
};

export default function StatsPage() {
  const { can } = useAuth();
  const { data, reload } = useFetch("/stats/series");
  const [sel, setSel] = useState(null);
  const [creating, setCreating] = useState(false);
  const back = useCallback(() => { setSel(null); reload(); }, [reload]);
  return (
    <>
      <PageHeader title="Statistiche eventi" subtitle="Confronto anno per anno degli eventi ricorrenti">
        {!sel && can("stats", "edit") && <Button data-testid="stats-new-record" className="h-11" onClick={() => setCreating(true)}><Plus className="h-4 w-4 mr-1" /> Nuovo consuntivo</Button>}
      </PageHeader>
      <StatsRecordDialog open={creating} onOpenChange={setCreating} onSaved={(s) => { reload(); setSel(s); }} />
      {sel ? <Detail name={sel} onBack={back} /> : !data ? <Spinner /> : (
        <div className="space-y-4">
          {data.items.length === 0 ? <Empty text="Nessun evento ricorrente: collega gli eventi con «Evento ricorrente» oppure crea un consuntivo per gli anni passati." testId="stats-empty" /> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.items.map((s) => (
                <button key={s.series} data-testid={`stats-series-${s.series}`} onClick={() => setSel(s.series)} className="text-left rounded-xl border border-slate-200 bg-white p-5 hover:border-primary transition-colors">
                  <BarChart3 className="h-5 w-5 text-primary mb-2" />
                  <div className="font-semibold text-slate-900">{s.series}</div>
                  <div className="text-sm text-slate-500">{s.events} {s.events === 1 ? "evento" : "eventi"}{s.records > 0 && ` · ${s.records} ${s.records === 1 ? "consuntivo" : "consuntivi"}`} · {s.years.join(", ")}</div>
                </button>
              ))}
            </div>
          )}
          {data.unlinked_events > 0 && <p className="text-sm text-slate-500" data-testid="stats-unlinked">{data.unlinked_events} eventi non sono collegati a una serie.</p>}
        </div>
      )}
    </>
  );
}
